import { useEffect } from 'react'
import { useSharedValue, withTiming, Easing, useAnimatedStyle, interpolate } from 'react-native-reanimated'
import { scheduleOnRN } from 'react-native-worklets'

const SHOW_BIG_PREVIEW_DURATION = 2000
const FLY_DURATION = 450

export const useFlyAndScaleAnimation = ({
  animationStarted,
  translateX,
  translateY,
  initialWidth,
  initialHeight,
  applyOpacity = true,
  onFinish
}: {
  animationStarted: boolean
  translateX: number
  translateY: number
  initialWidth: number
  initialHeight: number
  onFinish: () => void
  applyOpacity?: boolean
}) => {
  const fly = useSharedValue(0)

  const animatedStyle = useAnimatedStyle(() => {
    // Interpolating scale
    const scale = interpolate(fly.value, [0, 1], [1, 0.2])

    // Calculating adjusted translations to compensate for scaling
    const scaledWidth = initialWidth * scale
    const scaledHeight = initialHeight * scale

    const offsetX = (initialWidth - scaledWidth) / 2
    const offsetY = (initialHeight - scaledHeight) / 2

    // Adjusting translation values to reach the target correctly
    const adjustedTranslateX = interpolate(fly.value, [0, 1], [0, translateX - offsetX])
    const adjustedTranslateY = interpolate(fly.value, [0, 1], [0, translateY - offsetY])

    return {
      transform: [{ translateX: adjustedTranslateX }, { translateY: adjustedTranslateY }, { scale: scale }],
      ...(applyOpacity ? { opacity: interpolate(fly.value, [0, 1], [1, 0]) } : {})
    }
  }, [translateY, translateX, initialWidth, initialHeight])

  const onFinishAnimation = () => {
    onFinish()
  }

  useEffect(() => {
    if (animationStarted) {
      setTimeout(() => {
        fly.value = withTiming(
          1,
          {
            duration: FLY_DURATION,
            easing: Easing.bezier(0.25, 0.1, 0.25, 1)
          },
          (finished) => {
            if (finished) {
              scheduleOnRN(onFinishAnimation)
            }
          }
        )
      }, SHOW_BIG_PREVIEW_DURATION)
    } else {
      fly.value = 0
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [animationStarted])

  return {
    animatedStyle,
    animationStarted
  }
}
